import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import {
  appendFormulaTag,
  getNextNumberFormatFromFormulaKey,
  insertFormulaDataTagKeys,
} from "./insertFormulaHelpers";
import type { NumberFormatType } from "./insertFormulaTypes";
import {
  mapFormulaValidationMessage,
  validateFormula,
} from "./insertFormulaValidation";

interface FormulaDialogProps {
  open: boolean;
  onCancel: () => void;
  onInsert: (props: {
    formula: string;
    numberFormat: NumberFormatType;
    abs: boolean;
  }) => void;
  initialValues?: {
    formula: string;
    numberFormat: NumberFormatType;
    abs: boolean;
  };
}

interface ValidationState {
  hasError: boolean;
  displayMessage: string;
}

const DEFAULT_VALIDATION_STATE: ValidationState = {
  hasError: false,
  displayMessage: "",
};

const InsertFormulaDialog: React.FC<FormulaDialogProps> = ({
  open,
  onInsert,
  onCancel,
  initialValues,
}) => {
  const initialFormulaState = {
    numberFormat: initialValues?.numberFormat ?? ("currency" as const),
    formulaText: initialValues?.formula ?? "",
    abs: initialValues?.abs ?? false,
  };
  const initialFormulaStateKey = initialValues
    ? `${initialValues.numberFormat}:${initialValues.formula}:${initialValues.abs ? "1" : "0"}`
    : "default";
  const [formulaState, setFormulaState] = useState<{
    key: string;
    numberFormat: NumberFormatType;
    formulaText: string;
    abs: boolean;
  }>({
    key: "",
    ...initialFormulaState,
  });
  const [validationState, setValidationState] = useState<ValidationState>(
    DEFAULT_VALIDATION_STATE,
  );
  const activeFormulaState =
    formulaState.key === initialFormulaStateKey
      ? formulaState
      : {
          key: initialFormulaStateKey,
          ...initialFormulaState,
        };
  const numberFormat = activeFormulaState.numberFormat;
  const formulaText = activeFormulaState.formulaText;
  const abs = activeFormulaState.abs;
  const setNumberFormat = (value: NumberFormatType) => {
    setFormulaState((currentState) => ({
      ...activeFormulaState,
      ...currentState,
      key: initialFormulaStateKey,
      numberFormat: value,
    }));
  };
  const setFormulaText = (
    value: string | ((currentFormula: string) => string),
  ) => {
    setFormulaState((currentState) => {
      const baseState =
        currentState.key === initialFormulaStateKey
          ? currentState
          : activeFormulaState;
      const nextFormulaText =
        typeof value === "function" ? value(baseState.formulaText) : value;

      return {
        ...baseState,
        key: initialFormulaStateKey,
        formulaText: nextFormulaText,
      };
    });
  };
  const setAbs = (value: boolean) => {
    setFormulaState((currentState) => ({
      ...activeFormulaState,
      ...currentState,
      key: initialFormulaStateKey,
      abs: value,
    }));
  };

  const handleInsert = () => {
    const validationError = validateFormula(formulaText, numberFormat);
    if (validationError) {
      setValidationState({
        hasError: true,
        displayMessage: mapFormulaValidationMessage(validationError.message),
      });
      return;
    }

    setValidationState(DEFAULT_VALIDATION_STATE);
    onInsert({
      formula: formulaText,
      numberFormat,
      abs,
    });
    setFormulaText("");
  };

  const handleTagSelection = (selectedTag: string) => {
    setFormulaText((currentFormula) =>
      appendFormulaTag(currentFormula, selectedTag),
    );
  };

  const handleFormulaKeyDown = (key: string) => {
    const nextNumberFormat = getNextNumberFormatFromFormulaKey(
      key,
      formulaText,
      numberFormat,
    );

    if (nextNumberFormat !== numberFormat) {
      setNumberFormat(nextNumberFormat);
    }
  };

  const inputBoxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputBoxRef.current?.focus();
  }, [formulaText]);

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>Insert formula</DialogTitle>
      <DialogContent>
        <Alert severity="info" role="note">
          Note: Use formulas sparingly. Generally it is better to let the app
          provide the data to the user. However this feature is provided for
          flexibility so you can add context to the educational content by
          quoting actual values.
          <ul>
            <li>
              Be sure to check the result on the app against the known values
              (e.g. from a chart, table, or figure elsewhere on the app)
            </li>
            <li>
              The more complex a formula, the more likely something is to go
              wrong. Keep it simple, if possible.
            </li>
          </ul>
        </Alert>
        <Typography variant="body1">
          You may use any common mathematical operator (+, -, *, /). Use the
          dropdown list to insert fields.
        </Typography>
        <Box component="section" sx={{ mt: 1 }}>
          <Typography variant="body1">Examples:</Typography>
          <Box component="ul" sx={{ mt: 0.5 }}>
            <li>
              [REVENUE_LATEST_MONTH] / [REVENUE_PREVIOUS_MONTH] - 1 (gives the
              percentage change in revenue from the previous month)
            </li>
            <li>
              [REVENUE_LATEST_MONTH] - [REVENUE_PREVIOUS_MONTH] (gives the
              difference in revenue from the previous month)
            </li>
          </Box>
        </Box>
        <Stack direction="column" spacing={1}>
          <FormControl fullWidth>
            <InputLabel id="formula-field-picker-label">
              Available fields
            </InputLabel>
            <Select
              labelId="formula-field-picker-label"
              label="Available fields"
              value=""
              onChange={(event) => {
                handleTagSelection(String(event.target.value));
                event.target.value = "";
              }}
            >
              {insertFormulaDataTagKeys.map((key) => (
                <MenuItem key={key} value={key}>
                  {key}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select an item to insert it in the formula box.
            </FormHelperText>
          </FormControl>
          <TextField
            inputRef={inputBoxRef}
            value={formulaText}
            onChange={(event) => setFormulaText(event.target.value)}
            label="Formula"
            error={validationState.hasError}
            helperText={
              validationState.hasError
                ? validationState.displayMessage
                : undefined
            }
            slotProps={{
              formHelperText: {
                role: validationState.hasError ? "alert" : undefined,
              },
            }}
            fullWidth
            onKeyDown={(event) => handleFormulaKeyDown(event.key)}
          />
          <InputLabel id="link-type">Format result as:</InputLabel>
          <Select
            labelId="link-type"
            value={numberFormat}
            onChange={(event) => setNumberFormat(event.target.value)}
            fullWidth
          >
            <MenuItem value="currency">Currency</MenuItem>
            <MenuItem value="percent">Percent</MenuItem>
            <MenuItem value="text">Text</MenuItem>
          </Select>
          <FormControlLabel
            control={
              <Checkbox
                checked={abs}
                onChange={(event) => setAbs(event.target.checked)}
                slotProps={{
                  input: {
                    "aria-describedby": "formula-absolute-value-help",
                  },
                }}
              />
            }
            label="Absolute value"
          />
          <Typography
            id="formula-absolute-value-help"
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Use this when the wording should not show plus or minus.
          </Typography>
        </Stack>
      </DialogContent>
      <Stack direction="row" spacing={1}>
        <IconButton color="error" onClick={onCancel}>
          <CancelIcon />
          Cancel
        </IconButton>
        <IconButton color="success" onClick={handleInsert}>
          <CheckCircleIcon />
          Insert
        </IconButton>
      </Stack>
    </Dialog>
  );
};

export default InsertFormulaDialog;
export type { NumberFormatType } from "./insertFormulaTypes";
export {
  mapFormulaValidationMessage,
  validateFormula,
} from "./insertFormulaValidation";
